import { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../api';
import OfferDeskNav from '../components/OfferDeskNav';
import JobCard from '../components/jobs/JobCard';
import JobRail from '../components/jobs/JobRail';
import CompanyLogo from '../components/jobs/CompanyLogo';
import MatchRing from '../components/jobs/MatchRing';
import {
  CATEGORIES, CAT, isClosingSoon, daysLeft, daysUntil, isNew, buildSkillFrequency,
  SearchIcon, CloseIcon, SparkIcon, ArrowRightIcon, LOGO_TRADEMARK_NOTICE,
} from '../components/jobs/jobCardUtils';
import {
  applyModeLabel, applyModeStyle, resolveSupportTierKey, supportTierCompact, supportTierStyle,
} from '../utils/applyDisclosure';

const VIEW_KEY = 'tandm_jobs_view';

/* ─── Category chips ─────────────────────────────────────── */
function SelectorChips({ options, selected, onToggle }) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {options.map((option) => {
        const isSelected = selected === option;
        const cfg = CAT[option];
        return (
          <motion.button
            key={option}
            type="button"
            onClick={() => onToggle(option)}
            initial={false}
            animate={{
              backgroundColor: isSelected ? '#4E6C7E' : '#FFFFFF',
              borderColor: isSelected ? '#3A5468' : '#D9E2E6',
              color: isSelected ? '#FFFFFF' : '#475569',
            }}
            transition={{ duration: 0.14 }}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '7px 14px', borderRadius: 100, fontSize: 12, fontWeight: 600,
              border: '1.5px solid', cursor: 'pointer', fontFamily: 'var(--tandm-font-sans)',
              boxShadow: isSelected ? '0 4px 14px rgba(78,108,126,0.25)' : 'none',
            }}
          >
            {cfg && <cfg.icon color={isSelected ? '#fff' : cfg.accent} />}
            <span>{option}</span>
          </motion.button>
        );
      })}
    </div>
  );
}

function SkeletonCard({ delay }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay, duration: 0.4 }}
      style={{ background: 'var(--tandm-surface)', border: '1px solid var(--tandm-line)', borderRadius: 16, padding: 20 }}
    >
      {[['55%', 11], ['80%', 15], ['110px', 24], ['70px', 11], ['55px', 11]].map(([w, h], i) => (
        <div key={i} style={{
          height: h, width: w, borderRadius: 8, marginBottom: 10,
          background: 'linear-gradient(90deg,var(--tandm-line-soft) 25%,var(--tandm-canvas-deep) 50%,var(--tandm-line-soft) 75%)',
          backgroundSize: '400px 100%', animation: 'shimmer 1.3s ease infinite',
        }} />
      ))}
    </motion.div>
  );
}

function RecommendationsPanel() {
  const [recs, setRecs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/ai/recommendations')
      .then((r) => setRecs(r.data.recommendations || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ background: 'var(--tandm-surface-dark)', borderRadius: 16, padding: '20px 24px', marginBottom: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
        <div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.15)', borderTopColor: 'var(--tandm-accent-hot)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
        <span style={{ fontSize: 13, color: 'var(--tandm-on-dark-soft)' }}>Finding your best matches...</span>
      </div>
    );
  }
  if (!recs.length) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      style={{ background: 'var(--tandm-gradient-hero-dark)', borderRadius: 16, padding: '22px 24px', marginBottom: 24, position: 'relative', overflow: 'hidden' }}
    >
      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 65% 120% at 100% 50%,rgba(78,108,126,0.35) 0%,transparent 70%)', pointerEvents: 'none' }} />
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--tandm-on-dark)' }}>Picked for you</span>
          <span style={{ fontSize: 10, fontWeight: 700, background: 'var(--tandm-gradient-cta)', color: '#fff', padding: '2px 9px', borderRadius: 100, textTransform: 'uppercase', letterSpacing: '0.05em', display: 'flex', alignItems: 'center', gap: 4 }}>
            <SparkIcon /> AI
          </span>
          <span style={{ fontSize: 11, color: 'var(--tandm-on-dark-soft)', marginLeft: 'auto' }}>Based on your CV</span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(175px,1fr))', gap: 9 }}>
          {recs.map((rec, i) => (
            <motion.div key={rec.id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.07 }} whileHover={{ y: -3 }}>
              <Link to={`/jobs/${rec.id}`} style={{ display: 'block', textDecoration: 'none', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: 13 }}>
                <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--tandm-on-dark)', margin: '0 0 2px', lineHeight: 1.3 }}>{rec.title}</p>
                <p style={{ fontSize: 11, color: 'var(--tandm-on-dark-soft)', margin: '0 0 7px' }}>{rec.company_name}</p>
                <p style={{ fontSize: 11, color: 'var(--tandm-accent-light)', margin: 0, lineHeight: 1.4, fontStyle: 'italic' }}>&ldquo;{rec.reason}&rdquo;</p>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

/* ─── Dense list row (list view) ─────────────────────────── */
function JobListRow({ job, matchScore }) {
  const cat = CAT[job.category] || CAT.Other;
  const mode = job.apply_mode || 'manual';
  const modeStyle = applyModeStyle(mode);
  const tierKey = resolveSupportTierKey(job);
  const tierCompactVal = supportTierCompact(tierKey);
  const tierStyle = tierCompactVal ? supportTierStyle(tierCompactVal) : null;

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <Link
        to={`/jobs/${job.id}`}
        style={{
          display: 'flex', alignItems: 'center', gap: 14, textDecoration: 'none',
          background: 'var(--tandm-surface)', border: '1px solid var(--tandm-line)',
          borderRadius: 12, padding: '12px 16px', transition: 'border-color 0.15s, box-shadow 0.2s',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--tandm-accent-light)'; e.currentTarget.style.boxShadow = 'var(--tandm-shadow-soft)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--tandm-line)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        <CompanyLogo name={job.company_name} website={job.company_website} logoUrl={job.company_logo} accent={cat.accent} size={38} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13.5, fontWeight: 700, color: 'var(--tandm-ink)' }}>{job.title}</span>
            <span style={{ fontSize: 12, color: 'var(--tandm-muted)' }}>{job.company_name}</span>
          </div>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 3, alignItems: 'center' }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: cat.accent }}>{job.category}</span>
            <span style={{ fontSize: 11, fontWeight: 600, background: modeStyle.bg, color: modeStyle.color, padding: '1px 8px', borderRadius: 6 }}>{applyModeLabel(mode)}</span>
            {tierCompactVal && <span style={{ fontSize: 11, fontWeight: 700, background: tierStyle.bg, color: tierStyle.color, border: `1px solid ${tierStyle.border}`, padding: '1px 8px', borderRadius: 6 }}>{tierCompactVal}</span>}
            {job.location && <span style={{ fontSize: 11, color: 'var(--tandm-faint)' }}>{job.location}</span>}
          </div>
        </div>
        {job.deadline && (
          <span style={{ fontSize: 11, fontWeight: isClosingSoon(job.deadline) ? 600 : 400, color: isClosingSoon(job.deadline) ? 'var(--tandm-danger-fg)' : 'var(--tandm-faint)', whiteSpace: 'nowrap', flexShrink: 0 }}>
            {daysLeft(job.deadline)}
          </span>
        )}
        {matchScore != null && <MatchRing score={matchScore} size={38} stroke={3.5} />}
        <span style={{ color: 'var(--tandm-accent)', display: 'flex', flexShrink: 0 }}><ArrowRightIcon /></span>
      </Link>
    </motion.div>
  );
}

function ViewToggle({ view, onChange }) {
  const btn = (active) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 28,
    border: 'none', cursor: 'pointer', borderRadius: 8,
    background: active ? 'var(--tandm-surface)' : 'transparent',
    color: active ? 'var(--tandm-accent-deep)' : 'var(--tandm-faint)',
    boxShadow: active ? 'var(--tandm-shadow-soft)' : 'none', transition: 'all 0.15s',
  });
  return (
    <div style={{ display: 'flex', gap: 2, padding: 3, background: 'var(--tandm-canvas-deep)', borderRadius: 11, border: '1px solid var(--tandm-line)' }}>
      <button type="button" aria-label="Grid view" style={btn(view === 'grid')} onClick={() => onChange('grid')}>
        <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" /><rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" /></svg>
      </button>
      <button type="button" aria-label="List view" style={btn(view === 'list')} onClick={() => onChange('list')}>
        <svg width="15" height="15" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></svg>
      </button>
    </div>
  );
}

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [matchScores, setMatchScores] = useState({});
  const [view, setView] = useState(() => {
    try { return localStorage.getItem(VIEW_KEY) || 'grid'; } catch { return 'grid'; }
  });

  useEffect(() => {
    fetchJobs();
    fetchMatchScores();
  }, []);
  useEffect(() => { applyFilters(); }, [jobs, search, activeCategory, matchScores]); // eslint-disable-line react-hooks/exhaustive-deps

  const setViewPersist = (v) => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* ignore */ }
  };

  const fetchJobs = async () => {
    try {
      const res = await api.get('/internships');
      setJobs(res.data.internships || []);
    } catch { /* ignore */ } finally { setLoading(false); }
  };
  const fetchMatchScores = async () => {
    try {
      const res = await api.get('/ai/match-scores');
      setMatchScores(res.data.scores || {});
    } catch { /* ignore */ }
  };
  const scoreOf = (j) => matchScores[String(j.id)] ?? matchScores[j.id] ?? 0;
  const applyFilters = () => {
    let result = [...jobs];
    if (activeCategory !== 'All') result = result.filter((j) => j.category === activeCategory);
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter((j) => j.title?.toLowerCase().includes(q) || j.company_name?.toLowerCase().includes(q));
    }
    result.sort((a, b) => scoreOf(b) - scoreOf(a));
    setFiltered(result);
  };
  const handleChipToggle = (cat) => setActiveCategory((prev) => (prev === cat ? 'All' : cat));

  const uniqueCompanies = [...new Set(jobs.map((j) => j.company_name))].length;
  const hasScores = Object.keys(matchScores).length > 0;
  const strongMatches = Object.values(matchScores).filter((s) => s >= 70).length;
  const isBrowsingAll = activeCategory === 'All' && !search.trim();

  // Skill frequency across all roles → lets each card show its most distinctive
  // skills in the hover, instead of the same common ones everywhere.
  const skillFreq = useMemo(() => buildSkillFrequency(jobs), [jobs]);

  // Editorial rails (only when browsing the full list)
  const rails = useMemo(() => {
    const strong = [...jobs]
      .filter((j) => scoreOf(j) >= 70)
      .sort((a, b) => scoreOf(b) - scoreOf(a))
      .slice(0, 6);
    const closing = [...jobs]
      .filter((j) => { const d = daysUntil(j.deadline); return d != null && d >= 0 && d <= 7; })
      .sort((a, b) => daysUntil(a.deadline) - daysUntil(b.deadline))
      .slice(0, 6);
    const fresh = [...jobs]
      .filter((j) => isNew(j.created_at))
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
      .slice(0, 6);
    return { strong, closing, fresh };
  }, [jobs, matchScores]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ minHeight: '100vh', background: 'var(--tandm-canvas)', fontFamily: 'var(--tandm-font-sans)', color: 'var(--tandm-ink)' }}>
      <style>{`
        @keyframes shimmer { 0%{background-position:-400px 0} 100%{background-position:400px 0} }
        @keyframes spin { to { transform: rotate(360deg) } }
        * { box-sizing: border-box; }
        ::-webkit-scrollbar { width: 6px; height: 8px; }
        ::-webkit-scrollbar-thumb { background: rgba(78,108,126,0.35); border-radius: 3px; }
        input::placeholder { color: var(--tandm-faint); }
        .job-rail::-webkit-scrollbar { height: 7px; }
        @media (max-width: 720px) {
          .jobs-hero-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
        }
      `}</style>

      <OfferDeskNav />

      <div style={{ paddingTop: 'var(--tandm-nav-clearance, 112px)', paddingLeft: 24, paddingRight: 24, paddingBottom: 40, position: 'relative' }}>
        <div style={{ maxWidth: 1000, margin: '0 auto' }}>
          <div className="jobs-hero-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 40, alignItems: 'end' }}>
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1 style={{ fontFamily: 'var(--tandm-font-display)', fontSize: 'clamp(28px,4vw,42px)', fontWeight: 600, color: 'var(--tandm-ink)', letterSpacing: '-0.03em', lineHeight: 1.12, margin: '0 0 8px' }}>
                Find your<br />
                <span style={{ color: 'var(--tandm-accent)' }}>finance internship.</span>
              </h1>
              {!loading && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.3 }} style={{ display: 'flex', gap: 22, marginTop: 24 }}>
                  {[
                    { n: jobs.length, l: 'Open roles' },
                    { n: uniqueCompanies, l: 'Companies' },
                    ...(hasScores ? [{ n: strongMatches, l: 'Strong matches' }] : []),
                  ].map((s, i) => (
                    <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 2, ...(i > 0 ? { paddingLeft: 22, borderLeft: '1px solid var(--tandm-line)' } : {}) }}>
                      <span style={{ fontSize: 22, fontWeight: 800, color: 'var(--tandm-ink)', letterSpacing: '-0.03em' }}>{s.n}</span>
                      <span style={{ fontSize: 10, color: 'var(--tandm-muted)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em' }}>{s.l}</span>
                    </div>
                  ))}
                </motion.div>
              )}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}>
              <div style={{ position: 'relative' }}>
                <span style={{ position: 'absolute', left: 15, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none', display: 'flex' }}>
                  <SearchIcon color="var(--tandm-faint)" />
                </span>
                <input
                  type="text" placeholder="Search roles or companies..." value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: '100%', padding: '14px 44px', background: 'var(--tandm-surface)', border: '1px solid var(--tandm-line)', borderRadius: 14, fontSize: 14, fontFamily: 'var(--tandm-font-sans)', color: 'var(--tandm-ink)', outline: 'none', boxShadow: 'var(--tandm-shadow-soft)' }}
                  onFocus={(e) => { e.target.style.borderColor = 'var(--tandm-accent)'; }}
                  onBlur={(e) => { e.target.style.borderColor = 'var(--tandm-line)'; }}
                />
                {search && (
                  <button type="button" onClick={() => setSearch('')} style={{ position: 'absolute', right: 13, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--tandm-muted)', display: 'flex', padding: 0 }}>
                    <CloseIcon />
                  </button>
                )}
              </div>
              {hasScores && (
                <p style={{ fontSize: 11, color: 'var(--tandm-muted)', margin: '8px 0 0 4px', display: 'flex', alignItems: 'center', gap: 5 }}>
                  <SparkIcon /> Match scores calculated from your CV
                </p>
              )}
            </motion.div>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '8px 24px 60px' }}>
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }} style={{ marginBottom: 24 }}>
          <SelectorChips options={CATEGORIES} selected={activeCategory} onToggle={handleChipToggle} />
        </motion.div>

        <RecommendationsPanel />

        {/* Editorial rails — only when browsing the full list */}
        {!loading && isBrowsingAll && (
          <>
            {hasScores && <JobRail grid title="Strong matches for you" hint="Based on your CV" jobs={rails.strong} matchScores={matchScores} skillFreq={skillFreq} />}
            <JobRail grid title="Closing this week" hint="Apply before the deadline" jobs={rails.closing} matchScores={matchScores} skillFreq={skillFreq} />
            <JobRail grid title="New this week" jobs={rails.fresh} matchScores={matchScores} skillFreq={skillFreq} />
          </>
        )}

        {/* Results header + view toggle */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <h2 style={{ fontFamily: 'var(--tandm-font-display)', fontSize: 19, fontWeight: 500, color: 'var(--tandm-ink)', letterSpacing: '-0.02em', margin: 0 }}>
            {isBrowsingAll ? 'All internships' : 'Results'}
          </h2>
          <AnimatePresence>
            {(search || activeCategory !== 'All') && !loading && (
              <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} style={{ fontSize: 12, color: 'var(--tandm-muted)' }}>
                {filtered.length} result{filtered.length !== 1 ? 's' : ''}
                {activeCategory !== 'All' ? ` in ${activeCategory}` : ''}
                {search ? ` for "${search}"` : ''}
              </motion.span>
            )}
          </AnimatePresence>
          <div style={{ marginLeft: 'auto' }}>
            <ViewToggle view={view} onChange={setViewPersist} />
          </div>
        </div>

        {loading ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(310px,1fr))', gap: 18 }}>
            {[...Array(6)].map((_, i) => <SkeletonCard key={i} delay={i * 0.06} />)}
          </div>
        ) : filtered.length === 0 ? (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '80px 24px', textAlign: 'center' }}>
            <div style={{ width: 60, height: 60, background: 'var(--tandm-accent-subtle)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16, border: '1px solid var(--tandm-line)' }}>
              <SearchIcon color="var(--tandm-accent)" size={22} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--tandm-ink)', marginBottom: 6 }}>No internships found</p>
            <p style={{ fontSize: 13, color: 'var(--tandm-muted)' }}>Try a different search or category</p>
          </motion.div>
        ) : view === 'list' ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {filtered.map((job) => (
              <JobListRow key={job.id} job={job} matchScore={matchScores[String(job.id)] ?? matchScores[job.id] ?? null} />
            ))}
          </div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(310px,1fr))', gap: 18 }}>
            <AnimatePresence>
              {filtered.map((job, i) => (
                <motion.div key={job.id} style={{ display: 'flex' }} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3, delay: (i % 9) * 0.04 }}>
                  <JobCard job={job} matchScore={matchScores[String(job.id)] ?? matchScores[job.id] ?? null} skillFreq={skillFreq} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Trademark notice */}
        <p style={{ fontSize: 11, color: 'var(--tandm-faint)', lineHeight: 1.6, textAlign: 'center', maxWidth: 680, margin: '48px auto 0' }}>
          {LOGO_TRADEMARK_NOTICE}
        </p>
      </div>
    </div>
  );
}
